import { Scene } from "@babylonjs/core/scene";
import { ListenableEvent } from "../../utils";
import { removeElement } from "../../utils/array";
import { isNullOrEmpty } from "../../utils/string";
import { addToScene, removeFromScene } from "../utils/MeshUtils";
import { BasicElement } from "./BasicElement";
import { GeometryManager } from "./GeometryManager";
import { LayerManager } from "./LayerManager";
import { MaterialManager } from "./MaterialManager";
import { NodeUserData } from "./NodeUserData";
import { ElementChangedFlags } from "./SceneElement";
import { SelectionPropertyProvider } from "./SelectionPropertyProvider";
export class ModelManager {
    get app() {
        return (this.mApp);
    }
    get scene() {
        return (this.mScene);
    }
    get geometries() {
        return (this.mGeometryManager);
    }
    get materials() {
        return (this.mMaterialManager);
    }
    get layers() {
        return (this.mLayerManager);
    }
    /**
     * array of top level scene elements
     */
    get elements() {
        return (this.mElements);
    }
    /**
     * array of selected scene elements
     */
    get selection() {
        return (this.mSelection);
    }
    get selectionProperties() {
        return (this.mSelectionProperties);
    }
    /**
     * This mesh array contains all meshes which belong to the scene and are not disposed (but maybe removed from the scene).
     * The difference between this and Scene.meshes is that Scene.meshes does not
     * contain meshes which were removed from the scene.
     */
    get meshInstances() {
        return (this.mMeshInstances);
    }
    constructor(pApp) {
        this.eventSelectionChanged = new ListenableEvent(this);
        this.eventModified = new ListenableEvent(this);
        this.eventBeginTransformElements = new ListenableEvent(this);
        this.eventEndTransformElements = new ListenableEvent(this);
        this.onMeshAddedToScene = (pMesh, pEventState) => {
            if (pMesh != null && NodeUserData.GetData(pMesh, ModelManager.NODEDATA_MESHADDED) != true) {
                this.mMeshInstances.push(pMesh);
                NodeUserData.SetData(pMesh, ModelManager.NODEDATA_MESHADDED, true);
                pMesh.onDisposeObservable.add(this.onMeshDisposed);
            }
        };
        this.onMeshDisposed = (pMesh, pEventState) => {
            if (pMesh != null) {
                NodeUserData.SetData(pMesh, ModelManager.NODEDATA_MESHADDED, false);
                removeElement(this.mMeshInstances, pMesh);
                //clean up caches
                if (this.mCleanUpCachesOnDispose) {
                    this.mGeometryManager._cleanUpCache(false);
                    this.mMaterialManager._cleanUpCache(false);
                }
            }
        };
        this.mApp = pApp;
        this.mScene = pApp.scene;
        this.mElements = new Array();
        this.mMeshInstances = new Array();
        this.mCleanUpCachesOnDispose = true;
        this.mSelection = new Array();
        this.mSubElementSelection = new Map();
        this.mSelectionProperties = new SelectionPropertyProvider(pApp);
        this.mSelectionChanged = false;
        this.mSelectionChangeDepth = 0;
        this.mModified = false;
        this.mModificationDepth = 0;
        this.mScene.onNewMeshAddedObservable.add(this.onMeshAddedToScene);
        this.mGeometryManager = new GeometryManager(this);
        this.mMaterialManager = new MaterialManager(pApp);
        this.mLayerManager = new LayerManager(pApp);
        this.mLoaders = new Map();
        BasicElement._RegisterClass(this);
        //override Babylon DefaultMaterialFactory to use our default material and prevent creation of Babylon StandardMaterial
        Scene.DefaultMaterialFactory = () => {
            return (this.mMaterialManager.defaultMaterial);
        };
    }
    addElement(pElement, pNodeParent = null, pAddToScene = false) {
        if (pElement != null && pElement.node != null) {
            const tNode = pElement.node;
            if (pNodeParent == null) //root element
                this.mElements.push(pElement);
            else
                tNode.parent = pNodeParent;
            if (pAddToScene)
                addToScene(tNode, this.mScene);
            pElement.notifyChange(ElementChangedFlags.Added);
        }
    }
    removeElement(pElement, pRemoveFromScene = true, pDisposeElement = true) {
        if (pElement != null && pElement.node != null) {
            const tNode = pElement.node;
            if (pElement.getParentElement() == null) //root element
                removeElement(this.mElements, pElement);
            else
                //reset node parent to remove the node from the parents children list
                tNode.parent = null;
            if (pRemoveFromScene && !pDisposeElement)
                removeFromScene(pElement.node, this.mScene);
            if (pElement.isSelected)
                this.removeFromSelection(pElement);
            pElement.notifyChange(ElementChangedFlags.Removed);
            if (pDisposeElement)
                pElement.dispose();
        }
    }
    clear() {
        try {
            this.mCleanUpCachesOnDispose = false;
            for (const tElement of this.mElements)
                tElement.dispose();
            this.mElements = new Array();
            this.mGeometryManager.clear();
            this.mMaterialManager.clear();
            this.mLayerManager.clear();
            this.setSelection(null);
            this.notifyModification();
        }
        finally {
            this.mCleanUpCachesOnDispose = true;
        }
    }
    setSelection(pSelection) {
        let tChanged = false;
        //deselect first
        if (this.mSelection.length > 0) {
            for (const tElement of this.mSelection) {
                tElement._setIsSelected(false);
                this.mSubElementSelection.delete(tElement);
            }
            this.mSelection = new Array();
            tChanged = true;
        }
        if (pSelection != null && pSelection.length > 0) {
            for (const tElement of pSelection) {
                if (tElement.canSelect() && !tElement.isSelected) {
                    this.mSelection.push(tElement);
                    tElement._setIsSelected(true);
                }
            }
            tChanged = true;
        }
        if (tChanged)
            this.selectionChanged();
        return (tChanged);
    }
    addToSelection(pElement) {
        if (pElement == null || pElement.isSelected || !pElement.canSelect())
            return (false);
        this.mSelection.push(pElement);
        pElement._setIsSelected(true);
        this.selectionChanged();
        return (true);
    }
    removeFromSelection(pElement) {
        if (pElement == null || !pElement.isSelected)
            return (false);
        if (removeElement(this.mSelection, pElement)) {
            pElement._setIsSelected(false);
            this.mSubElementSelection.delete(pElement);
            this.selectionChanged();
            return (true);
        }
        return (false);
    }
    /**
     * Selects a sub element for the given main element. The main element has to be selected already.
     */
    setSubElementSelection(pMainElement, pSubElement) {
        if (pMainElement == null || !pMainElement.isSelected)
            return (false);
        let tResult = true;
        let tSubElement = pSubElement;
        if (!pMainElement.canSelectSubElement() ||
            (pSubElement != null && !pSubElement.canSelect())) {
            tResult = false;
            tSubElement = null;
        }
        if (tSubElement != this.getSubElementSelection(pMainElement)) {
            if (tSubElement == null)
                this.mSubElementSelection.delete(pMainElement);
            else
                this.mSubElementSelection.set(pMainElement, tSubElement);
            this.selectionChanged();
        }
        return (tResult);
    }
    getSubElementSelection(pMainElement) {
        const tElement = this.mSubElementSelection.get(pMainElement);
        return (tElement != null ? tElement : null);
    }
    /**
     * Selects and returns the parent selectable sub element based on the current sub element
     * selection for the given main element. If no sub element is found the main element will
     * be selected and null will be returned.
     */
    selectParentSubElement(pMainElement) {
        const tSubElement = this.getSubElementSelection(pMainElement);
        if (tSubElement == null)
            return (null);
        const tParent = tSubElement.getSelectableParentElement();
        if (tParent === pMainElement) {
            this.setSubElementSelection(pMainElement, null);
            return (null);
        }
        else {
            this.setSubElementSelection(pMainElement, tParent);
            return (tParent);
        }
    }
    clearSubElementSelection() {
        if (this.hasSubElementSelection()) {
            this.mSubElementSelection.clear();
            this.selectionChanged();
        }
    }
    hasSubElementSelection() {
        return (this.mSubElementSelection.size > 0);
    }
    validateSelection() {
        if (this.mSelection.length === 0)
            return;
        this.beginSelectionChange();
        const tNewSelection = new Array();
        const tNewSubElementSelection = new Array();
        for (const tElement of this.mSelection) {
            if (tElement.canSelect()) {
                tNewSelection.push(tElement);
                tNewSubElementSelection.push(this.getSubElementSelection(tElement));
            }
        }
        if (this.mSelection.length != tNewSelection.length)
            this.setSelection(tNewSelection);
        //restore/validate sub element selection
        for (let i = 0; i < tNewSelection.length; ++i) {
            if (tNewSubElementSelection[i] != null)
                this.setSubElementSelection(tNewSelection[i], tNewSubElementSelection[i]);
        }
        this.endSelectionChange();
    }
    beginSelectionChange() {
        this.mSelectionChangeDepth++;
    }
    endSelectionChange() {
        this.mSelectionChangeDepth--;
        if (this.mSelectionChangeDepth <= 0) {
            this.mSelectionChangeDepth = 0;
            if (this.mSelectionChanged)
                this.selectionChanged();
        }
    }
    notifyModification() {
        this.mModified = true;
        if (this.mModificationDepth === 0) {
            this.mModified = false;
            this.eventModified.trigger();
        }
    }
    beginModification() {
        this.mModificationDepth++;
    }
    endModification() {
        this.mModificationDepth--;
        if (this.mModificationDepth <= 0) {
            this.mModificationDepth = 0;
            if (this.mModified)
                this.notifyModification();
        }
    }
    registerElementLoader(pClassId, pLoader) {
        if (isNullOrEmpty(pClassId) || pLoader == null)
            return;
        this.mLoaders.set(pClassId, pLoader);
    }
    getElementLoader(pClassId) {
        const tLoader = this.mLoaders.get(pClassId);
        return (tLoader != null ? tLoader : null);
    }
    selectionChanged() {
        this.mSelectionChanged = true;
        if (this.mSelectionChangeDepth === 0) {
            this.mSelectionChanged = false;
            this.mSelectionProperties.setProviders(this.getSelectedProviders());
            this.eventSelectionChanged.trigger();
        }
    }
    getSelectedProviders() {
        if (this.mSubElementSelection.size === 0)
            return (this.mSelection);
        const tProviders = new Array();
        for (const tElement of this.mSelection) {
            const tSub = this.getSubElementSelection(tElement);
            tProviders.push(tSub != null ? tSub : tElement);
        }
        return (tProviders);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
ModelManager.NODEDATA_MESHADDED = "core:meshAddedToModel";
